import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
// Context
import { useAuth } from 'AuthContext';
import useClubContext from 'contexts/Club';
import GoalsContext from './GoalsContext';
// Services
import {
  createGoalEntry,
  updateGoalEntry,
  deleteGoalEntry,
  getGoalEntries,
  getGoalProgress,
  createMilestone,
  deleteMilestone,
  updateMilestone,
  pauseGoal as pauseGoalApi,
  resumeGoal as resumeGoalApi,
  retireGoal as retireGoalApi,
  reactivateGoal as reactivateGoalApi,
} from 'services/goals/goals.service';
// Hooks
import { useGoals, useCreateGoal, useUpdateGoal, useDeleteGoal } from 'hooks/useGoals';
// Utils
import { normalizeGoalType } from 'utils/goalHelpers';

// Helper function to normalize goal data
const normalizeGoal = (goalData, docId = null, preserveEntries = false) => {
  // Ensure milestones array is properly set and is always an array
  if (!goalData.milestones && goalData.Milestones) {
    goalData.milestones = goalData.Milestones;
  }
  if (!goalData.milestones) {
    goalData.milestones = [];
  }
  // Ensure milestones have required properties
  if (Array.isArray(goalData.milestones)) {
    goalData.milestones = goalData.milestones.map(m => ({
      ...m,
      id: m.id || m.ID,
      done: m.done || false,
      title: m.title || 'Untitled milestone',
      order: m.order !== undefined ? m.order : (m.id || m.ID || 0)
    }));
  }
  // Normalize goal type
  goalData.type = normalizeGoalType(goalData.type);
  
  const normalized = {
    id: docId || goalData.id,
    ...goalData,
    clubGoalId: goalData.clubGoalId ?? goalData.club_goal_id ?? null,
    progressDirection: goalData.progressDirection ?? goalData.progress_direction ?? 'increase',
  };
  
  // Preserve entries and pagination if they exist and preserveEntries is true
  // This allows us to keep cached entries when updating goals
  if (!preserveEntries) {
    // Initialize entries and pagination if not present
    if (!normalized.entries) {
      normalized.entries = [];
    }
    if (!normalized.entriesPagination) {
      normalized.entriesPagination = {
        hasMore: true,
        offset: 0,
        limit: 10
      };
    }
  }
  
  return normalized;
};

const GOALS_LIST_STORAGE_KEY = 'goals_list';

const getStoredGoalsList = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(GOALS_LIST_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const goals = Array.isArray(parsed.goals) ? parsed.goals : [];
    return { clubId: parsed.clubId, userId: parsed.userId, goals };
  } catch {
    return null;
  }
};

const setStoredGoalsList = (clubId, userId, goals) => {
  if (typeof window === 'undefined' || !clubId || !userId) return;
  try {
    sessionStorage.setItem(GOALS_LIST_STORAGE_KEY, JSON.stringify({
      clubId,
      userId,
      goals: Array.isArray(goals) ? goals : [],
    }));
  } catch {
    // ignore quota or parse errors
  }
};

let _goalsRestoreCache = null;

// ******************STATE VALUES**********************
const GoalsProvider = ({ children }) => {
  const { user } = useAuth();
  const { currentClub, refreshClubMembers } = useClubContext();
  const queryClient = useQueryClient();
  const [goals, setGoals] = useState(() => {
    if (!_goalsRestoreCache) _goalsRestoreCache = getStoredGoalsList();
    return _goalsRestoreCache?.goals ?? [];
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const goalEntriesCacheRef = useRef({});
  const goalsRef = useRef(goals);

  const {
    data: goalsData,
    isLoading,
    error: queryError,
    refetch,
  } = useGoals(user?.uid, currentClub?.id);

  const createGoalMutation = useCreateGoal(user?.uid, currentClub?.id);
  const updateGoalMutation = useUpdateGoal(user?.uid, currentClub?.id);
  const deleteGoalMutation = useDeleteGoal(user?.uid, currentClub?.id);

  // ******************LOAD FUNCTIONS**********************
  // Fetch goals from API (cache is kept in sync via mutation invalidation)
  const refreshGoals = useCallback(async () => {
    if (!user || !currentClub) return;
    try {
      await refetch();
    } catch (err) {
      setError('Failed to fetch goals');
      console.error('Error fetching goals:', err);
    }
  }, [user, currentClub, refetch]);

  // ******************EFFECTS/REACTIONS**********************
  useEffect(() => {
    if (goalsData) {
      setGoals(goalsData);
    }
  }, [goalsData]);

  useEffect(() => {
    goalsRef.current = goals;
  }, [goals]);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (queryError) {
      setError('Failed to fetch goals');
    } else {
      setError(null);
    }
  }, [queryError]);

  // Clear restored state if stored goals were for a different club/user
  useEffect(() => {
    if (!currentClub?.id || !user?.uid) return;
    const stored = getStoredGoalsList();
    if (stored && (stored.clubId !== currentClub.id || stored.userId !== user.uid)) {
      setGoals([]);
    }
  }, [currentClub?.id, user?.uid]);

  // Persist goals list so we can restore on remount (avoid empty flash)
  useEffect(() => {
    if (!currentClub?.id || !user?.uid) return;
    setStoredGoalsList(currentClub.id, user.uid, goals);
  }, [currentClub?.id, user?.uid, goals]);

  useEffect(() => () => { _goalsRestoreCache = null }, []);

  // ******************UTILITY FUNCTIONS**********************

  /**
   * Centralized cache sync for club-linked goal mutations. Optimistically
   * patches the React Query caches that hold club-goal snapshots so every
   * surface (dashboard card, detail modal, breakdown) updates from a single
   * mutation without waiting for a network refetch. A background invalidation
   * runs after for eventual consistency with the server.
   *
   * @param {object} params
   * @param {number|string} params.clubGoalId Club goal id this mutation affected.
   * @param {number} [params.actualDelta] Signed change to aggregate.actual (metric goals).
   * @param {number} [params.completedMembersDelta] Signed change to completedMembers (one_time / habit).
   * @param {number} [params.milestonesDoneDelta] Signed change to milestones done (milestone goals).
   */
  const syncClubGoalCaches = useCallback(
    ({
      clubGoalId,
      actualDelta = 0,
      completedMembersDelta = 0,
      milestonesDoneDelta = 0,
    }) => {
      if (!clubGoalId) return;

      const patchSnapshot = (snap) => {
        if (!snap) return snap;
        const next = { ...snap };
        const target = Number(next.target) || 0;

        if (actualDelta && (next.label === 'remaining_budget' || next.label === 'sum_quantity')) {
          next.actual = Math.max(0, (Number(next.actual) || 0) + actualDelta);
          if (next.label === 'remaining_budget') {
            next.percent =
              target > 0 ? Math.min(100, ((target - next.actual) / target) * 100) : 0;
          } else {
            next.percent = target > 0 ? Math.min(100, (next.actual / target) * 100) : 0;
          }
        }

        if (
          completedMembersDelta &&
          (next.label === 'members_completed' || next.label === 'members_completed_period')
        ) {
          const totalMembers = Number(next.totalMembers) || 0;
          next.actual = Math.max(0, (Number(next.actual) || 0) + completedMembersDelta);
          next.completedMembers = next.actual;
          next.percent = totalMembers > 0 ? (next.actual / totalMembers) * 100 : 0;
        }

        if (milestonesDoneDelta && next.label === 'milestones_done') {
          next.actual = Math.max(0, (Number(next.actual) || 0) + milestonesDoneDelta);
          next.percent = target > 0 ? (next.actual / target) * 100 : 0;
        }

        return next;
      };

      queryClient.setQueriesData(
        { queryKey: ['clubGoalOverview'] },
        (oldData) => {
          if (!oldData?.clubGoals) return oldData;
          return {
            ...oldData,
            clubGoals: oldData.clubGoals.map((cg) =>
              Number(cg.id) === Number(clubGoalId)
                ? { ...cg, snapshot: patchSnapshot(cg.snapshot) }
                : cg,
            ),
          };
        },
      );

      queryClient.setQueriesData(
        { queryKey: ['clubGoalProgress'] },
        (oldData) => {
          if (!oldData?.aggregate) return oldData;
          if (Number(oldData?.clubGoal?.id) !== Number(clubGoalId)) return oldData;
          return { ...oldData, aggregate: patchSnapshot(oldData.aggregate) };
        },
      );

      queryClient.invalidateQueries({ queryKey: ['clubGoalOverview'] });
      queryClient.invalidateQueries({ queryKey: ['clubGoalProgress'] });
      queryClient.invalidateQueries({ queryKey: ['clubGoalBreakdown'] });
      queryClient.invalidateQueries({ queryKey: ['clubGoalEntries'] });
    },
    [queryClient],
  );

  // ******************SETTERS**********************
  // Add a new goal (mutations invalidate goals query so cache and remounts stay in sync)
  const handleAddGoal = useCallback(async (goalData) => {
    if (!user || !currentClub) return;

    try {
      setError(null);
      const result = await createGoalMutation.mutateAsync(goalData);
      const newGoal = normalizeGoal(result, result.id);
      return newGoal;
    } catch (err) {
      setError('Failed to create goal');
      console.error('Error creating goal:', err);
      throw err;
    }
  }, [user, currentClub, createGoalMutation]);

  // Update an existing goal
  const handleUpdateGoal = useCallback(async (goalId, updates) => {
    if (!user || !currentClub) return;

    try {
      setError(null);
      const priorGoal = goalsRef.current.find((g) => g.id === goalId);
      const priorCompleted = !!priorGoal?.completed;
      const result = await updateGoalMutation.mutateAsync({ goalId, updates });
      const updatedGoal = normalizeGoal(result, result.id, true);
      // Preserve entries and pagination when updating (they're cached separately)
      let updatedGoalResult = null;
      setGoals(prev => prev.map(goal => {
        if (goal.id === goalId) {
          updatedGoalResult = {
            ...updatedGoal,
            entries: goal.entries || updatedGoal.entries || [],
            entriesPagination: goal.entriesPagination || updatedGoal.entriesPagination || {
              hasMore: true,
              offset: 0,
              limit: 10,
            },
          };
          return updatedGoalResult;
        }
        return goal;
      }));

      const clubGoalId = priorGoal?.clubGoalId ?? updatedGoal?.clubGoalId;
      if (clubGoalId && priorGoal?.type === 'one_time') {
        const nextCompleted = updates?.completed != null
          ? !!updates.completed
          : !!updatedGoal?.completed;
        if (nextCompleted !== priorCompleted) {
          syncClubGoalCaches({
            clubGoalId,
            completedMembersDelta: nextCompleted ? 1 : -1,
          });
        }
      }

      return updatedGoalResult || updatedGoal;
    } catch (err) {
      setError('Failed to update goal');
      console.error('Error updating goal:', err);
      throw err;
    }
  }, [user, currentClub, updateGoalMutation, syncClubGoalCaches]);

  // Delete a goal
  const handleDeleteGoal = useCallback(async (goalId) => {
    if (!user || !currentClub) return;

    let deletedGoal = null;
    setGoals(prev => {
      deletedGoal = prev.find(g => g.id === goalId);
      return prev.filter(goal => goal.id !== goalId);
    });

    try {
      setError(null);
      await deleteGoalMutation.mutateAsync(goalId);
    } catch (err) {
      setError('Failed to delete goal');
      console.error('Error deleting goal:', err);
      if (deletedGoal) {
        setGoals(prev => [...prev, deletedGoal]);
      }
      throw err;
    }
  }, [user, currentClub, deleteGoalMutation]);

  const handlePauseGoal = useCallback(async (goalId) => {
    if (!user || !currentClub) return;

    try {
      setError(null);
      const result = await pauseGoalApi(user.uid, currentClub.id, goalId);
      const updatedGoal = normalizeGoal(result, result.id, true);
      let updatedGoalResult = null;
      setGoals(prev => prev.map(goal => {
        if (goal.id === goalId) {
          updatedGoalResult = {
            ...updatedGoal,
            entries: goal.entries || updatedGoal.entries || [],
            entriesPagination: goal.entriesPagination || updatedGoal.entriesPagination || {
              hasMore: true,
              offset: 0,
              limit: 10,
            },
          };
          return updatedGoalResult;
        }
        return goal;
      }));
      queryClient.invalidateQueries({ queryKey: ['goals', user.uid, currentClub.id] });
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
      refreshClubMembers(currentClub.id, true);
      return updatedGoalResult || updatedGoal;
    } catch (err) {
      setError('Failed to pause goal');
      console.error('Error pausing goal:', err);
      throw err;
    }
  }, [user, currentClub, queryClient, refreshClubMembers]);

  const handleResumeGoal = useCallback(async (goalId) => {
    if (!user || !currentClub) return;

    try {
      setError(null);
      const result = await resumeGoalApi(user.uid, currentClub.id, goalId);
      const updatedGoal = normalizeGoal(result, result.id, true);
      let updatedGoalResult = null;
      setGoals(prev => prev.map(goal => {
        if (goal.id === goalId) {
          updatedGoalResult = {
            ...updatedGoal,
            entries: goal.entries || updatedGoal.entries || [],
            entriesPagination: goal.entriesPagination || updatedGoal.entriesPagination || {
              hasMore: true,
              offset: 0,
              limit: 10,
            },
          };
          return updatedGoalResult;
        }
        return goal;
      }));
      queryClient.invalidateQueries({ queryKey: ['goals', user.uid, currentClub.id] });
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
      refreshClubMembers(currentClub.id, true);
      return updatedGoalResult || updatedGoal;
    } catch (err) {
      setError('Failed to resume goal');
      console.error('Error resuming goal:', err);
      throw err;
    }
  }, [user, currentClub, queryClient, refreshClubMembers]);

  const handleRetireGoal = useCallback(async (goalId) => {
    if (!user || !currentClub) return;

    try {
      setError(null);
      const result = await retireGoalApi(user.uid, currentClub.id, goalId);
      const updatedGoal = normalizeGoal(result, result.id, true);
      setGoals(prev => prev.filter(goal => goal.id !== goalId));
      queryClient.invalidateQueries({ queryKey: ['goals', user.uid, currentClub.id] });
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
      return updatedGoal;
    } catch (err) {
      setError('Failed to retire goal');
      console.error('Error retiring goal:', err);
      throw err;
    }
  }, [user, currentClub, queryClient]);

  const handleReactivateGoal = useCallback(async (goalId) => {
    if (!user || !currentClub) return;

    try {
      setError(null);
      const result = await reactivateGoalApi(user.uid, currentClub.id, goalId);
      const updatedGoal = normalizeGoal(result, result.id, true);
      let updatedGoalResult = null;
      setGoals(prev => {
        const existing = prev.find(g => g.id === goalId);
        if (existing) {
          return prev.map(goal => {
            if (goal.id === goalId) {
              updatedGoalResult = {
                ...updatedGoal,
                entries: goal.entries || updatedGoal.entries || [],
                entriesPagination: goal.entriesPagination || updatedGoal.entriesPagination || {
                  hasMore: true,
                  offset: 0,
                  limit: 10,
                },
              };
              return updatedGoalResult;
            }
            return goal;
          });
        }
        updatedGoalResult = updatedGoal;
        return [...prev, updatedGoal];
      });
      queryClient.invalidateQueries({ queryKey: ['goals', user.uid, currentClub.id] });
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
      return updatedGoalResult || updatedGoal;
    } catch (err) {
      setError('Failed to reactivate goal');
      console.error('Error reactivating goal:', err);
      throw err;
    }
  }, [user, currentClub, queryClient]);

  // ******************ENTRY OPERATIONS**********************
  const clearGoalEntriesCache = useCallback((goalId) => {
    if (!goalId || !goalEntriesCacheRef.current[goalId]) return;
    delete goalEntriesCacheRef.current[goalId];
  }, []);

  const handleFetchGoalEntriesForMonth = useCallback(async (goalId, monthDate) => {
    if (!user || !goalId || !monthDate) return [];

    const date = monthDate instanceof Date ? monthDate : new Date(monthDate);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const goalCache = goalEntriesCacheRef.current[goalId] || {};

    if (goalCache[monthKey]) {
      return goalCache[monthKey];
    }

    const periodStart = new Date(date.getFullYear(), date.getMonth(), 1);
    const periodEnd = new Date(date.getFullYear(), date.getMonth() + 1, 1);

    const entries = await getGoalEntries(user.uid, goalId, periodStart, periodEnd);
    const sortedEntries = entries.sort((a, b) => {
      const dateA = new Date(a.occurred_at || a.occurredAt || 0);
      const dateB = new Date(b.occurred_at || b.occurredAt || 0);
      return dateB - dateA;
    });

    goalEntriesCacheRef.current[goalId] = {
      ...goalCache,
      [monthKey]: sortedEntries,
    };

    return sortedEntries;
  }, [user]);

  const handleFetchGoalEntriesAll = useCallback(async (goalId) => {
    if (!user || !goalId) return [];

    const goalCache = goalEntriesCacheRef.current[goalId] || {};
    if (goalCache.all) {
      return goalCache.all;
    }

    const entries = await getGoalEntries(user.uid, goalId);
    const sortedEntries = entries.sort((a, b) => {
      const dateA = new Date(a.occurred_at || a.occurredAt || 0);
      const dateB = new Date(b.occurred_at || b.occurredAt || 0);
      return dateB - dateA;
    });

    goalEntriesCacheRef.current[goalId] = {
      ...goalCache,
      all: sortedEntries,
    };

    return sortedEntries;
  }, [user]);

  // Fetch entries for a goal (with pagination support)
  const handleFetchGoalEntries = useCallback(async (goalId, limit = 10, offset = 0, append = false, periodStart = null, periodEnd = null) => {
    if (!user || !goalId) return;

    try {
      const entries = await getGoalEntries(user.uid, goalId, periodStart, periodEnd, limit, offset);
      
      // Sort entries by occurred_at (descending - most recent first)
      const sortedEntries = entries.sort((a, b) => {
        const dateA = new Date(a.occurred_at || a.occurredAt || 0);
        const dateB = new Date(b.occurred_at || b.occurredAt || 0);
        return dateB - dateA;
      });

      setGoals(prev => prev.map(goal => {
        if (goal.id === goalId) {
          const existingEntries = goal.entries || [];
          const newEntries = append 
            ? [...existingEntries, ...sortedEntries].sort((a, b) => {
                const dateA = new Date(a.occurred_at || a.occurredAt || 0);
                const dateB = new Date(b.occurred_at || b.occurredAt || 0);
                return dateB - dateA;
              })
            : sortedEntries;
          
          return {
            ...goal,
            entries: newEntries,
            entriesPagination: {
              hasMore: entries.length === limit,
              offset: offset + entries.length,
              limit
            }
          };
        }
        return goal;
      }));

      return sortedEntries;
    } catch (err) {
      console.error('Error fetching entries:', err);
      throw err;
    }
  }, [user]);

  // Create a new entry
  const handleCreateEntry = useCallback(async (goalId, entryData) => {
    if (!user || !currentClub || !goalId) return;

    try {
      const result = await createGoalEntry(user.uid, goalId, entryData);
      // Service returns {entry: <entry object>, goal: null, ...entry properties}
      const savedEntry = result.entry || result;
      const updatedGoal = result.goal;

      // Normalize entry to ensure consistent date format
      // Sequelize returns occurredAt (camelCase), but we need both formats for compatibility
      const normalizedEntry = {
        id: savedEntry.id,
        goalId: savedEntry.goalId || savedEntry.goal_id,
        userId: savedEntry.userId || savedEntry.user_id,
        occurred_at: savedEntry.occurred_at || savedEntry.occurredAt,
        occurredAt: savedEntry.occurredAt || savedEntry.occurred_at,
        quantity: savedEntry.quantity,
        created_at: savedEntry.created_at || savedEntry.createdAt,
      };

      let updatedProgress = null;
      try {
        updatedProgress = await getGoalProgress(user.uid, goalId);
      } catch (progressError) {
        console.error('Error fetching goal progress:', progressError);
      }

      // Update goal in state with new entry added to entries array
      setGoals(prev => {
        const updatedGoals = prev.map(goal => {
          if (goal.id === goalId) {
            const existingEntries = goal.entries || [];
            // Ensure we don't add duplicates - check by id
            const entryExists = existingEntries.some(e => e.id === normalizedEntry.id);
            
            if (entryExists) {
              // Entry already exists, update it (shouldn't happen for new entries, but just in case)
              const newEntries = existingEntries.map(e => 
                e.id === normalizedEntry.id ? normalizedEntry : e
              );
              
              // Sort by date (most recent first)
              const sortedEntries = [...newEntries].sort((a, b) => {
                const dateA = new Date(a.occurred_at || a.occurredAt || 0);
                const dateB = new Date(b.occurred_at || b.occurredAt || 0);
                return dateB - dateA;
              });
              
              return {
                ...goal,
                entries: sortedEntries, // New array reference
                progress: updatedProgress || goal.progress,
                entriesPagination: {
                  ...(goal.entriesPagination || {
                    hasMore: true,
                    offset: 0,
                    limit: 10
                  })
                }
              };
            } else {
              // Add new entry at the beginning
              const newEntries = [normalizedEntry, ...existingEntries];
              
              // Sort by date (most recent first)
              const sortedEntries = [...newEntries].sort((a, b) => {
                const dateA = new Date(a.occurred_at || a.occurredAt || 0);
                const dateB = new Date(b.occurred_at || b.occurredAt || 0);
                return dateB - dateA;
              });
              
              // Create a completely new goal object to ensure React detects the change
              // This is critical for React to re-render
              const updatedGoalObj = {
                ...goal,
                entries: sortedEntries, // New array reference
                progress: updatedProgress || goal.progress,
                entriesPagination: {
                  ...(goal.entriesPagination || {
                    hasMore: true,
                    offset: 0,
                    limit: 10
                  })
                }
              };
              
              // If API returned updated goal, merge its properties
              if (updatedGoal) {
                const normalizedUpdatedGoal = normalizeGoal(updatedGoal, updatedGoal.id, true);
                return {
                  ...normalizedUpdatedGoal,
                  entries: sortedEntries, // Use the same sorted entries
                  progress: updatedProgress || normalizedUpdatedGoal.progress || goal.progress,
                  entriesPagination: updatedGoalObj.entriesPagination
                };
              }
              
              return updatedGoalObj;
            }
          }
          return goal; // Return unchanged goal (new reference not needed for other goals)
        });
        
        // Return new array to ensure React detects the change
        return updatedGoals;
      });

      clearGoalEntriesCache(goalId);
      refreshClubMembers(currentClub.id, true);

      // Keep club-goal caches in lock-step with the goals store so all
      // surfaces (dashboard card, detail modal, breakdown) re-render from a
      // single mutation without a refetch.
      const sourceGoal = goalsRef.current.find((g) => g.id === goalId);
      const clubGoalId = sourceGoal?.clubGoalId;
      if (clubGoalId) {
        const quantity = Number(entryData?.quantity) || 0;
        const isMetric = sourceGoal?.type === 'metric';
        syncClubGoalCaches({
          clubGoalId,
          actualDelta: isMetric ? quantity : 0,
        });
      }

      return { entry: normalizedEntry, goal: updatedGoal };
    } catch (err) {
      console.error('Error creating entry:', err);
      throw err;
    }
  }, [user, currentClub, refreshClubMembers, syncClubGoalCaches]);

  // Update an existing entry
  const handleUpdateEntry = useCallback(async (goalId, entryId, updates) => {
    if (!user || !currentClub || !goalId || !entryId) return;

    try {
      const priorGoal = goalsRef.current.find((g) => g.id === goalId);
      const priorEntry = priorGoal?.entries?.find((e) => e.id === entryId);
      const priorQty = Number(priorEntry?.quantity) || 0;

      const result = await updateGoalEntry(user.uid, goalId, entryId, updates);
      const savedEntry = result.entry || result;
      const updatedGoal = result.goal;

      // Update goal in state with entry updated in entries array
      setGoals(prev => prev.map(goal => {
        if (goal.id === goalId) {
          const existingEntries = goal.entries || [];
          const newEntries = existingEntries.map(e => 
            e.id === entryId ? { ...e, ...updates, ...savedEntry } : e
          ).sort((a, b) => {
            const dateA = new Date(a.occurred_at || a.occurredAt || 0);
            const dateB = new Date(b.occurred_at || b.occurredAt || 0);
            return dateB - dateA;
          });
          
          const normalizedGoal = updatedGoal
            ? normalizeGoal(updatedGoal, updatedGoal.id, true)
            : goal;

          return {
            ...normalizedGoal,
            entries: newEntries,
            entriesPagination: goal.entriesPagination || {
              hasMore: true,
              offset: 0,
              limit: 10
            }
          };
        }
        return goal;
      }));

      clearGoalEntriesCache(goalId);

      const clubGoalId = priorGoal?.clubGoalId;
      if (clubGoalId) {
        const nextQty = Number(updates?.quantity ?? savedEntry?.quantity ?? priorQty) || 0;
        const isMetric = priorGoal?.type === 'metric';
        syncClubGoalCaches({
          clubGoalId,
          actualDelta: isMetric ? nextQty - priorQty : 0,
        });
      }

      return { entry: savedEntry, goal: updatedGoal };
    } catch (err) {
      console.error('Error updating entry:', err);
      throw err;
    }
  }, [user, currentClub, syncClubGoalCaches]);

  // Delete an entry
  const handleDeleteEntry = useCallback(async (goalId, entryId) => {
    if (!user || !currentClub || !goalId || !entryId) return;

    try {
      const priorGoal = goalsRef.current.find((g) => g.id === goalId);
      const priorEntry = priorGoal?.entries?.find((e) => e.id === entryId);
      const priorQty = Number(priorEntry?.quantity) || 0;

      // DELETE endpoint returns 204 (no content), so we handle it optimistically
      await deleteGoalEntry(user.uid, goalId, entryId);

      let updatedProgress = null;
      try {
        updatedProgress = await getGoalProgress(user.uid, goalId);
      } catch (progressError) {
        console.error('Error fetching goal progress:', progressError);
      }

      // Update goal in state with entry removed from entries array
      let updatedGoalResult = null;
      setGoals(prev => prev.map(goal => {
        if (goal.id === goalId) {
          const existingEntries = goal.entries || [];
          const newEntries = existingEntries.filter(e => e.id !== entryId);
          
          // Preserve existing goal data, just update entries
          updatedGoalResult = {
            ...goal,
            entries: newEntries,
            progress: updatedProgress || goal.progress,
            entriesPagination: goal.entriesPagination || {
              hasMore: true,
              offset: 0,
              limit: 10
            }
          };
          return updatedGoalResult;
        }
        return goal;
      }));

      clearGoalEntriesCache(goalId);

      const clubGoalId = priorGoal?.clubGoalId;
      if (clubGoalId) {
        const isMetric = priorGoal?.type === 'metric';
        syncClubGoalCaches({
          clubGoalId,
          actualDelta: isMetric ? -priorQty : 0,
        });
      }

      return updatedGoalResult;
    } catch (err) {
      console.error('Error deleting entry:', err);
      throw err;
    }
  }, [user, currentClub, syncClubGoalCaches]);

  // ******************MILESTONE OPERATIONS**********************
  // Create a new milestone
  const handleCreateMilestone = useCallback(async (goalId, milestoneData) => {
    if (!user || !currentClub || !goalId) return;

    try {
      const result = await createMilestone(user.uid, goalId, milestoneData);

      let createdMilestone = result;
      // Some APIs might wrap the response
      if (result && result.id === undefined && result.milestone) {
        createdMilestone = result.milestone;
      }

      setGoals(prev => prev.map(goal => {
        if (goal.id === goalId) {
          const existingMilestones = goal.milestones || [];
          const combinedMilestones = [...existingMilestones, createdMilestone];
          const sortedMilestones = [...combinedMilestones].sort((a, b) => {
            const orderA = a.order !== undefined ? a.order : (a.id || 0);
            const orderB = b.order !== undefined ? b.order : (b.id || 0);
            return orderA - orderB;
          }).map((milestone, index) => ({
            ...milestone,
            order: index,
          }));

          const completedCount = sortedMilestones.filter(m => m.done).length;
          const progress = {
            completed: sortedMilestones.length > 0 && completedCount === sortedMilestones.length,
            actual: completedCount,
            target: sortedMilestones.length,
          };

          return {
            ...goal,
            milestones: sortedMilestones,
            progress,
          };
        }
        return goal;
      }));

      const priorGoal = goalsRef.current.find((g) => g.id === goalId);
      if (priorGoal?.clubGoalId) {
        syncClubGoalCaches({ clubGoalId: priorGoal.clubGoalId });
      }

      return createdMilestone;
    } catch (err) {
      console.error('Error creating milestone:', err);
      throw err;
    }
  }, [user, currentClub, syncClubGoalCaches]);

  // Delete a milestone
  const handleDeleteMilestone = useCallback(async (goalId, milestoneId) => {
    if (!user || !currentClub || !goalId || !milestoneId) return;

    try {
      const priorGoal = goalsRef.current.find((g) => g.id === goalId);
      const milestoneIdNumForLookup =
        typeof milestoneId === 'string' ? parseInt(milestoneId, 10) : milestoneId;
      const priorMilestone = (priorGoal?.milestones || []).find((m) => {
        const mId = m.id || m.ID;
        const mIdNum = typeof mId === 'string' ? parseInt(mId, 10) : mId;
        return mIdNum === milestoneIdNumForLookup;
      });
      const priorDoneRemoved = !!priorMilestone?.done;

      await deleteMilestone(user.uid, goalId, milestoneId);

      setGoals(prev => prev.map(goal => {
        if (goal.id === goalId) {
          const targetId = typeof milestoneId === 'string' ? parseInt(milestoneId, 10) : milestoneId;
          const remainingMilestones = (goal.milestones || []).filter(m => {
            const mId = m.id || m.ID;
            const normalizedId = typeof mId === 'string' ? parseInt(mId, 10) : mId;
            return normalizedId !== targetId;
          }).map((milestone, index) => ({
            ...milestone,
            order: index,
          }));

          const completedCount = remainingMilestones.filter(m => m.done).length;
          const progress = {
            completed: remainingMilestones.length > 0 && completedCount === remainingMilestones.length,
            actual: completedCount,
            target: remainingMilestones.length,
          };

          return {
            ...goal,
            milestones: remainingMilestones,
            progress,
          };
        }
        return goal;
      }));

      const clubGoalId = priorGoal?.clubGoalId;
      if (clubGoalId && priorDoneRemoved) {
        syncClubGoalCaches({
          clubGoalId,
          milestonesDoneDelta: -1,
        });
      } else if (clubGoalId) {
        syncClubGoalCaches({ clubGoalId });
      }
    } catch (err) {
      console.error('Error deleting milestone:', err);
      throw err;
    }
  }, [user, currentClub, syncClubGoalCaches]);

  // Update a single milestone
  const handleUpdateMilestone = useCallback(async (goalId, milestoneId, updates) => {
    if (!user || !currentClub || !goalId || !milestoneId) return;

    try {
      const priorGoal = goalsRef.current.find((g) => g.id === goalId);
      const milestoneIdNumForLookup =
        typeof milestoneId === 'string' ? parseInt(milestoneId, 10) : milestoneId;
      const priorMilestone = (priorGoal?.milestones || []).find((m) => {
        const mId = m.id || m.ID;
        const mIdNum = typeof mId === 'string' ? parseInt(mId, 10) : mId;
        return mIdNum === milestoneIdNumForLookup;
      });
      const priorDone = !!priorMilestone?.done;

      const updatedMilestone = await updateMilestone(user.uid, goalId, milestoneId, updates);
      
      // Update milestone in goal's milestones array and calculate progress locally
      let updatedGoalResult = null;
      setGoals(prev => prev.map(goal => {
        if (goal.id === goalId) {
          const updatedMilestones = (goal.milestones || []).map(m => {
            const mId = m.id || m.ID;
            const mIdNum = typeof mId === 'string' ? parseInt(mId, 10) : mId;
            const milestoneIdNum = typeof milestoneId === 'string' ? parseInt(milestoneId, 10) : milestoneId;
            
            if (mIdNum === milestoneIdNum) {
              // Use the updated milestone from API response directly
              // Backend handles doneAt clearing when done is false
              return {
                ...m,
                ...updatedMilestone,
                done: updatedMilestone.done || false,
                doneAt: updatedMilestone.doneAt || null
              };
            }
            return m;
          });
          
          // Calculate progress locally for milestone goals
          const allDone = updatedMilestones.length > 0 && updatedMilestones.every(m => m.done);
          const completedCount = updatedMilestones.filter(m => m.done).length;
          const progress = {
            completed: allDone,
            actual: completedCount,
            target: updatedMilestones.length
          };
          
          updatedGoalResult = {
            ...goal,
            milestones: updatedMilestones,
            progress: progress
          };
          
          return updatedGoalResult;
        }
        return goal;
      }));

      const clubGoalId = priorGoal?.clubGoalId;
      if (clubGoalId) {
        const nextDone = !!updatedMilestone?.done;
        if (nextDone !== priorDone) {
          syncClubGoalCaches({
            clubGoalId,
            milestonesDoneDelta: nextDone ? 1 : -1,
          });
        }
      }

      return updatedGoalResult;
    } catch (err) {
      console.error('Error updating milestone:', err);
      throw err;
    }
  }, [user, currentClub, syncClubGoalCaches]);

  // Bulk update milestones (for reordering)
  const handleBulkUpdateMilestones = useCallback(async (goalId, milestones) => {
    if (!user || !currentClub || !goalId) return;

    try {
      const result = await updateGoalMutation.mutateAsync({ goalId, updates: { milestones } });
      return normalizeGoal(result, result.id);
    } catch (err) {
      console.error('Error bulk updating milestones:', err);
      throw err;
    }
  }, [user, currentClub, updateGoalMutation]);

  // ******************EXPORTS**********************
  return (
    <GoalsContext.Provider
      value={{
        goals,
        loading,
        error,
        addGoal: handleAddGoal,
        updateGoal: handleUpdateGoal,
        pauseGoal: handlePauseGoal,
        resumeGoal: handleResumeGoal,
        retireGoal: handleRetireGoal,
        reactivateGoal: handleReactivateGoal,
        deleteGoal: handleDeleteGoal,
        refreshGoals,
        createEntry: handleCreateEntry,
        updateEntry: handleUpdateEntry,
        deleteEntry: handleDeleteEntry,
        fetchGoalEntries: handleFetchGoalEntries,
        fetchGoalEntriesForMonth: handleFetchGoalEntriesForMonth,
        fetchGoalEntriesAll: handleFetchGoalEntriesAll,
        createMilestone: handleCreateMilestone,
        deleteMilestone: handleDeleteMilestone,
        updateMilestone: handleUpdateMilestone,
        bulkUpdateMilestones: handleBulkUpdateMilestones,
        syncClubGoalCaches,
      }}
    >
      {children}
    </GoalsContext.Provider>
  );
};

export const useGoalsContext = () => {
  const context = React.useContext(GoalsContext);
  if (!context) {
    throw new Error('useGoalsContext must be used within a GoalsProvider');
  }
  return context;
};

export default GoalsProvider;
